import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Settings as SettingsIcon, Lock, Smartphone, Copy, Bell, Building2, Users, Database, Loader2, Hospital, Timer, UserCog, MessageSquare, Landmark, FileText, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EstablishmentManager } from "@/components/EstablishmentManager";
import { StaffManager } from "@/components/StaffManager";

const NOTIF_EVENTS = [
  { key: "new_ride", label: "Nouvelle course / affectation" },
  { key: "patient_request", label: "Réservation d'un patient via l'app" },
  { key: "match", label: "Correspondance trajet à vide (IA)" },
  { key: "delay", label: "Retard signalé" },
  { key: "missing_signature", label: "Signature PMTi manquante" },
  { key: "bourse", label: "Activité bourse d'échange" },
];

const SECTIONS = [
  { key: "tableau-de-bord", label: "Tableau de bord" },
  { key: "jeregul", label: "Jeregul" },
  { key: "laregul", label: "Planning" },
  { key: "corrections", label: "Rétroviseur" },
  { key: "tribus", label: "Tribus Pro" },
  { key: "bourse", label: "Bourse d'échange" },
  { key: "vehicules", label: "Véhicules" },
  { key: "conducteurs", label: "Conducteurs" },
  { key: "patients", label: "Patients & Clients" },
  { key: "export", label: "Export & Facturation" },
  { key: "statistiques", label: "Statistiques" },
  { key: "abonnement", label: "Abonnement" },
];

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ rate_per_km: 2.0, base_fare: 10.0, phone: "", address: "", logo_url: "", night_surcharge_pct: 0, sunday_surcharge_pct: 0, rest_optimization: false, share_time_window_min: 15, share_dest_radius_km: 2.0, share_pickup_overhead_min: 10, samu_mode: false, samu_lock_hours: 12, service_start: "06:00" });
  const [notif, setNotif] = useState(null);

  const [lockEnabled, setLockEnabled] = useState(false);
  const [lockedSections, setLockedSections] = useState([]);
  const [password, setPassword] = useState("");
  const [savingLock, setSavingLock] = useState(false);
  const [lockLoaded, setLockLoaded] = useState(false);

  useEffect(() => { api.get("/company/settings").then((r) => setForm(r.data)); }, []);
  useEffect(() => { api.get("/notif-prefs").then((r) => setNotif(r.data)); }, []);
  useEffect(() => {
    api.get("/security/section-lock").then((r) => {
      setLockEnabled(r.data.enabled);
      setLockedSections(r.data.locked_sections || []);
    }).finally(() => setLockLoaded(true));
  }, []);

  const save = async () => {
    try {
      await api.put("/company/settings", {
        rate_per_km: Number(form.rate_per_km), base_fare: Number(form.base_fare),
        phone: form.phone || "", address: form.address || "", logo_url: form.logo_url || "",
        night_surcharge_pct: Number(form.night_surcharge_pct) || 0,
        sunday_surcharge_pct: Number(form.sunday_surcharge_pct) || 0,
        rest_optimization: !!form.rest_optimization,
        share_time_window_min: Number(form.share_time_window_min) || 0,
        share_dest_radius_km: Number(form.share_dest_radius_km) || 0,
        share_pickup_overhead_min: Number(form.share_pickup_overhead_min) || 0,
        samu_mode: !!form.samu_mode,
        samu_lock_hours: Math.max(8, Math.min(12, Number(form.samu_lock_hours) || 12)),
        service_start: form.service_start || "06:00",
        pickup_ambulance_min: Number(form.pickup_ambulance_min ?? 15) || 0,
        dropoff_ambulance_min: Number(form.dropoff_ambulance_min ?? 20) || 0,
        pickup_assis_min: Number(form.pickup_assis_min ?? 10) || 0,
        dropoff_assis_min: Number(form.dropoff_assis_min ?? 15) || 0,
        meal_break_enabled: form.meal_break_enabled !== false,
        meal_break_min: Number(form.meal_break_min) || 30,
        meal_window_start: form.meal_window_start || "11:30",
        meal_window_end: form.meal_window_end || "14:00",
        overtime_tolerance_min: Number.isFinite(Number(form.overtime_tolerance_min)) ? Number(form.overtime_tolerance_min) : 120,
        max_wait_return_min: Math.max(15, Math.min(180, Number(form.max_wait_return_min) || 60)),
        sms_delay_enabled: !!form.sms_delay_enabled,
        sms_notify_patient: form.sms_notify_patient !== false,
        sms_notify_etab: !!form.sms_notify_etab,
        cpam_amb_forfait: Number(form.cpam_amb_forfait ?? 57) || 0,
        cpam_amb_km: Number(form.cpam_amb_km ?? 2.44) || 0,
        cpam_vsl_forfait: Number(form.cpam_vsl_forfait ?? 15.66) || 0,
        cpam_vsl_km: Number(form.cpam_vsl_km ?? 1.1) || 0,
        cpam_km_deduction: Number(form.cpam_km_deduction ?? 3) || 0,
        cpam_night_pct: Number(form.cpam_night_pct ?? 50) || 0,
        cpam_sunday_pct: Number(form.cpam_sunday_pct ?? 25) || 0,
        cpam_short_enabled: form.cpam_short_enabled !== false,
        cpam_short_t1: Number(form.cpam_short_t1 ?? 9.75) || 0,
        cpam_short_t2: Number(form.cpam_short_t2 ?? 7.53) || 0,
        cpam_short_t3: Number(form.cpam_short_t3 ?? 5.47) || 0,
        cpam_short_t4: Number(form.cpam_short_t4 ?? 3.42) || 0,
        cpam_use_dept_defaults: form.cpam_use_dept_defaults !== false,
      });
      toast.success("Réglages société enregistrés");
      if ((form.address || "").trim()) {
        try {
          const { data } = await api.post("/establishments/autopopulate?radius_km=50");
          if (data.added > 0) { toast.success(`${data.added} établissement(s) de santé détecté(s) autour de la société et ajouté(s)`); setEstabReload((x) => x + 1); }
        } catch { /* non bloquant */ }
      }
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveNotif = async (next) => {
    setNotif(next);
    try { await api.put("/notif-prefs", next); } catch (err) { toast.error(apiError(err)); }
  };

  const [demoBusy, setDemoBusy] = useState(false);
  const [estabReload, setEstabReload] = useState(0);
  const demoData = async (action) => {
    setDemoBusy(true);
    try {
      const { data } = await api.post(`/company/demo-data?action=${action}`);
      if (action === "clear") toast.success("Jeu de démo supprimé");
      else toast.success(`Jeu de démo généré : ${data.patients} patients · ${data.rides} courses · ${data.regul?.grouped || 0} mutualisée(s) · ${data.regul?.surcharge || 0} en surcharge`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setDemoBusy(false); }
  };

  const toggleSection = (key) => {
    setLockedSections((prev) => prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]);
  };

  const saveLock = async () => {
    if (lockEnabled && lockedSections.length === 0) {
      toast.error("Sélectionnez au moins une section à protéger");
      return;
    }
    setSavingLock(true);
    try {
      await api.put("/security/section-lock", {
        enabled: lockEnabled,
        password: password || undefined,
        locked_sections: lockedSections,
      });
      setPassword("");
      // Reset session unlocks so the new config takes effect immediately.
      Object.keys(sessionStorage).filter((k) => k.startsWith("pacemia_unlock_")).forEach((k) => sessionStorage.removeItem(k));
      await refreshUser();
      toast.success("Protection par code enregistrée");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingLock(false); }
  };

  return (
    <div className="space-y-6 max-w-lg">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <SettingsIcon className="h-6 w-6 text-primary" /> Réglages
        </h1>
        <p className="text-muted-foreground text-sm">Tarifs et sécurité de votre espace.</p>
      </div>

      <Card className="p-6 rounded-md shadow-none space-y-4">
        <h2 className="font-semibold flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Société & Tarifs</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Téléphone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="03 80 …" className="mt-1" data-testid="settings-phone" /></div>
          <div><Label className="text-xs">Logo (URL)</Label><Input value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…/logo.png" className="mt-1" data-testid="settings-logo" /></div>
          <div className="col-span-2"><Label className="text-xs">Adresse (siège, sur feuilles de route / reçus)</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" data-testid="settings-address" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Forfait de base (€)</Label><Input type="number" step="0.5" value={form.base_fare} onChange={(e) => setForm({ ...form, base_fare: e.target.value })} className="mt-1" data-testid="settings-base-fare" /></div>
          <div><Label className="text-xs">Tarif au km (€/km)</Label><Input type="number" step="0.1" value={form.rate_per_km} onChange={(e) => setForm({ ...form, rate_per_km: e.target.value })} className="mt-1" data-testid="settings-rate-km" /></div>
          <div><Label className="text-xs">Majoration nuit (%) <span className="text-muted-foreground">20h-8h</span></Label><Input type="number" step="5" value={form.night_surcharge_pct} onChange={(e) => setForm({ ...form, night_surcharge_pct: e.target.value })} className="mt-1" data-testid="settings-night" /></div>
          <div><Label className="text-xs">Majoration dimanche (%)</Label><Input type="number" step="5" value={form.sunday_surcharge_pct} onChange={(e) => setForm({ ...form, sunday_surcharge_pct: e.target.value })} className="mt-1" data-testid="settings-sunday" /></div>
        </div>
        <p className="text-sm text-muted-foreground">Ex : un trajet de 20 km = {(Number(form.base_fare) + Number(form.rate_per_km) * 20).toFixed(2)} € (hors majorations).</p>

        <label className="flex items-start gap-2 cursor-pointer border-t border-border pt-3">
          <Checkbox checked={!!form.rest_optimization} onCheckedChange={(v) => setForm({ ...form, rest_optimization: !!v })} data-testid="settings-rest-opt" />
          <span className="text-sm">
            <span className="font-medium">Optimiser le repos des chauffeurs</span>
            <span className="block text-xs text-muted-foreground">La régulation concentre les courses sur le moins de chauffeurs possible (sans dépasser l'amplitude horaire), pour libérer des journées de repos.</span>
          </span>
        </label>

        <Button onClick={save} data-testid="settings-save">Enregistrer</Button>
      </Card>

      <Card className="p-6 rounded-md shadow-none space-y-4" data-testid="settings-share-card">
        <h2 className="font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Transport groupé (mutualisation)</h2>
        <p className="text-sm text-muted-foreground">Règles utilisées par la régulation pour regrouper plusieurs patients dans un même véhicule et réduire les kilomètres à vide.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Fenêtre horaire (min)</Label>
            <Input type="number" min="0" step="5" value={form.share_time_window_min}
              onChange={(e) => setForm({ ...form, share_time_window_min: e.target.value })}
              className="mt-1" data-testid="settings-share-window" />
            <span className="block text-[11px] text-muted-foreground mt-1">Écart max entre les heures de RDV.</span>
          </div>
          <div>
            <Label className="text-xs">Rayon destination (km)</Label>
            <Input type="number" min="0" step="0.5" value={form.share_dest_radius_km}
              onChange={(e) => setForm({ ...form, share_dest_radius_km: e.target.value })}
              className="mt-1" data-testid="settings-share-radius" />
            <span className="block text-[11px] text-muted-foreground mt-1">Distance max entre les points de dépose.</span>
          </div>
          <div>
            <Label className="text-xs">Détour / patient (min)</Label>
            <Input type="number" min="0" step="1" value={form.share_pickup_overhead_min}
              onChange={(e) => setForm({ ...form, share_pickup_overhead_min: e.target.value })}
              className="mt-1" data-testid="settings-share-overhead" />
            <span className="block text-[11px] text-muted-foreground mt-1">Temps ajouté par patient supplémentaire.</span>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">Capacités par véhicule : VSL 3 · Taxi 3 · TPMR 2 · Ambulance 1 (allongé, non mutualisé).</p>

        <div className="border-t border-border pt-4 space-y-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="settings-samu-toggle">
            <Checkbox checked={!!form.samu_mode} onCheckedChange={(v) => setForm({ ...form, samu_mode: !!v })} data-testid="settings-samu-check" />
            <span className="font-medium">Mode SAMU / garde — bloquer un équipage ambulance</span>
          </label>
          <p className="text-xs text-muted-foreground">Une fois affecté, l'équipage ambulance reste bloqué sur son véhicule pour la durée de garde (indisponible pour d'autres courses).</p>
          {form.samu_mode && (
            <div className="w-48">
              <Label className="text-xs">Durée de blocage (heures)</Label>
              <Input type="number" min="8" max="12" value={form.samu_lock_hours}
                onChange={(e) => setForm({ ...form, samu_lock_hours: e.target.value })}
                className="mt-1" data-testid="settings-samu-hours" />
              <span className="block text-[11px] text-muted-foreground mt-1">Entre 8 h et 12 h.</span>
            </div>
          )}
        </div>

        <div className="border-t border-border pt-4">
          <Label className="text-xs">Début de service (alerte)</Label>
          <Input type="time" value={form.service_start} onChange={(e) => setForm({ ...form, service_start: e.target.value })} className="mt-1 w-48" data-testid="settings-service-start" />
          <span className="block text-[11px] text-muted-foreground mt-1">Une alerte (non bloquante) s'affiche si l'heure de départ calculée d'un chauffeur tombe avant cette heure.</span>
        </div>
        <Button onClick={save} data-testid="settings-share-save">Enregistrer</Button>
      </Card>

      <Card className="p-6 rounded-md shadow-none space-y-4" data-testid="settings-handling-card">
        <div>
          <h2 className="font-semibold flex items-center gap-2"><Timer className="h-4 w-4 text-primary" /> Temps de manutention & pause repas</h2>
          <p className="text-sm text-muted-foreground">Forfaits d'installation / brancardage / formalités ajoutés par la régulation à chaque course, et pause repas réglementaire (véhicule verrouillé pendant la pause).</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Ambulance — Prise en charge (min)</Label>
            <Input type="number" min="0" max="60" value={form.pickup_ambulance_min ?? 15}
              onChange={(e) => setForm({ ...form, pickup_ambulance_min: e.target.value })} className="mt-1" data-testid="settings-pickup-amb" />
          </div>
          <div>
            <Label className="text-xs">Ambulance — Dépose en service (min)</Label>
            <Input type="number" min="0" max="60" value={form.dropoff_ambulance_min ?? 20}
              onChange={(e) => setForm({ ...form, dropoff_ambulance_min: e.target.value })} className="mt-1" data-testid="settings-dropoff-amb" />
          </div>
          <div>
            <Label className="text-xs">VSL / Taxi / TPMR — Prise en charge (min)</Label>
            <Input type="number" min="0" max="60" value={form.pickup_assis_min ?? 10}
              onChange={(e) => setForm({ ...form, pickup_assis_min: e.target.value })} className="mt-1" data-testid="settings-pickup-assis" />
          </div>
          <div>
            <Label className="text-xs">VSL / Taxi / TPMR — Dépose en service (min)</Label>
            <Input type="number" min="0" max="60" value={form.dropoff_assis_min ?? 15}
              onChange={(e) => setForm({ ...form, dropoff_assis_min: e.target.value })} className="mt-1" data-testid="settings-dropoff-assis" />
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">L'heure d'arrivée au RDV = patient installé dans le service, pas le véhicule garé sur le parking.</p>

        <div className="border-t border-border pt-3 space-y-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="settings-meal-toggle">
            <Checkbox checked={form.meal_break_enabled !== false} onCheckedChange={(v) => setForm({ ...form, meal_break_enabled: !!v })} data-testid="settings-meal-check" />
            <span className="font-medium">Pause repas obligatoire — véhicule strictement indisponible</span>
          </label>
          {form.meal_break_enabled !== false && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Durée (min)</Label>
                <Select value={String(form.meal_break_min || 30)} onValueChange={(v) => setForm({ ...form, meal_break_min: Number(v) })}>
                  <SelectTrigger className="mt-1" data-testid="settings-meal-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Fenêtre — début</Label>
                <Input type="time" value={form.meal_window_start || "11:30"} onChange={(e) => setForm({ ...form, meal_window_start: e.target.value })} className="mt-1" data-testid="settings-meal-start" />
              </div>
              <div>
                <Label className="text-xs">Fenêtre — fin</Label>
                <Input type="time" value={form.meal_window_end || "14:00"} onChange={(e) => setForm({ ...form, meal_window_end: e.target.value })} className="mt-1" data-testid="settings-meal-end" />
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-border pt-3">
          <Label className="text-xs">Tolérance de dépassement d'amplitude (min)</Label>
          <Input type="number" min="0" max="240" value={form.overtime_tolerance_min ?? 120}
            onChange={(e) => setForm({ ...form, overtime_tolerance_min: e.target.value })} className="mt-1 w-48" data-testid="settings-overtime" />
          <span className="block text-[11px] text-muted-foreground mt-1">0 = interdiction stricte. Sinon, un dépassement est autorisé pour sauver une course critique, avec [ALERTE DÉPASSEMENT AMPLITUDE] motivée.</span>
        </div>
        <div className="border-t border-border pt-3">
          <Label className="text-xs">Attente max du patient après la fin de soin (min) — courses retour</Label>
          <Input type="number" min="15" max="180" value={form.max_wait_return_min ?? 60}
            onChange={(e) => setForm({ ...form, max_wait_return_min: e.target.value })} className="mt-1 w-48" data-testid="settings-max-wait-return" />
          <span className="block text-[11px] text-muted-foreground mt-1">La régulation prend en charge le patient au plus tôt après sa fin de soin, sans jamais dépasser ce plafond (sinon [ALERTE ATTENTE RETOUR]).</span>
        </div>
        <Button onClick={save} data-testid="settings-handling-save">Enregistrer</Button>
      </Card>

      <Card className="p-6 rounded-md shadow-none space-y-4" data-testid="settings-sms-card">
        <div>
          <h2 className="font-semibold flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" /> SMS de retour (Brevo)</h2>
          <p className="text-sm text-muted-foreground">
            Envoi automatique d'un SMS dès que le chauffeur passe une course retour « En route » :
            « [Société] : votre véhicule est en route pour vous ramener, prise en charge estimée à HH:MM ».
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={!!form.sms_delay_enabled} onCheckedChange={(v) => setForm({ ...form, sms_delay_enabled: !!v })} data-testid="settings-sms-toggle" />
          <span className="font-medium">Activer les SMS de retour</span>
        </label>
        {!!form.sms_delay_enabled && (
          <div className="space-y-2 pl-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={form.sms_notify_patient !== false} onCheckedChange={(v) => setForm({ ...form, sms_notify_patient: !!v })} data-testid="settings-sms-patient" />
              <span>Prévenir le patient (numéro de la course ou de la fiche)</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={!!form.sms_notify_etab} onCheckedChange={(v) => setForm({ ...form, sms_notify_etab: !!v })} data-testid="settings-sms-etab" />
              <span>Prévenir l'établissement (si la course vient d'une saisie hospitalière)</span>
            </label>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">
          Nécessite des crédits SMS dans votre compte Brevo (~0,045 €/SMS en France) et un expéditeur SMS
          enregistré (Brevo → Paramètres → SMS Sender ID). Envoi limité à un SMS par course, jamais bloquant.
        </p>
        <Button onClick={save} data-testid="settings-sms-save">Enregistrer</Button>
      </Card>

      <Card className="p-6 rounded-md shadow-none space-y-4" data-testid="settings-cpam-card">
        <div>
          <h2 className="font-semibold flex items-center gap-2"><Landmark className="h-4 w-4 text-primary" /> Tarifs conventionnés CPAM</h2>
          <p className="text-sm text-muted-foreground">
            Utilisés pour calculer le montant CPAM des courses sanitaires dans l'export comptable et le relevé mensuel :
            forfait + tarif kilométrique au-delà de l'abattement. Les tarifs varient par département — ajustez-les selon votre convention.
          </p>
        </div>
        {form.department && (
          <div className="rounded border border-primary/30 bg-primary/5 px-3 py-2 text-sm flex items-center justify-between" data-testid="settings-cpam-dept">
            <span>
              <span className="font-medium text-primary">Département détecté :</span> {form.department} — {form.department_name || "—"}
              {form.cpam_dept_defaults && (
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Défauts 2026 : Ambulance {form.cpam_dept_defaults.cpam_amb_forfait} €
                  {form.cpam_dept_defaults.cpam_amb_agglo && (
                    <span className="text-emerald-700 dark:text-emerald-300"> (forfait agglomération)</span>
                  )}
                  {" "}+ {form.cpam_dept_defaults.cpam_amb_km} €/km · VSL {form.cpam_dept_defaults.cpam_vsl_forfait} € + {form.cpam_dept_defaults.cpam_vsl_km} €/km · <span className="text-primary">Taxi conventionné {form.cpam_dept_defaults.cpam_taxi_km} €/km</span>
                </span>
              )}
            </span>
            <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="settings-cpam-use-dept-label">
              <Checkbox checked={form.cpam_use_dept_defaults !== false} onCheckedChange={(v) => setForm({ ...form, cpam_use_dept_defaults: !!v })} data-testid="settings-cpam-use-dept" />
              Appliquer les défauts du département
            </label>
          </div>
        )}
        {!form.department && (
          <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="settings-cpam-no-dept">
            Aucun département détecté — renseignez le code postal dans l'adresse société (ex : « 14 rue X, 21000 Dijon ») puis Enregistrez pour appliquer automatiquement les tarifs départementaux.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Ambulance — Forfait départemental (€)</Label>
            <Input type="number" min="0" step="0.01" value={form.cpam_amb_forfait ?? 57}
              onChange={(e) => setForm({ ...form, cpam_amb_forfait: e.target.value })} className="mt-1" data-testid="settings-cpam-amb-forfait" />
          </div>
          <div>
            <Label className="text-xs">Ambulance — Tarif kilométrique (€/km)</Label>
            <Input type="number" min="0" step="0.01" value={form.cpam_amb_km ?? 2.44}
              onChange={(e) => setForm({ ...form, cpam_amb_km: e.target.value })} className="mt-1" data-testid="settings-cpam-amb-km" />
          </div>
          <div>
            <Label className="text-xs">VSL / TPMR — Forfait (€)</Label>
            <Input type="number" min="0" step="0.01" value={form.cpam_vsl_forfait ?? 15.66}
              onChange={(e) => setForm({ ...form, cpam_vsl_forfait: e.target.value })} className="mt-1" data-testid="settings-cpam-vsl-forfait" />
          </div>
          <div>
            <Label className="text-xs">VSL / TPMR — Tarif kilométrique (€/km)</Label>
            <Input type="number" min="0" step="0.01" value={form.cpam_vsl_km ?? 1.10}
              onChange={(e) => setForm({ ...form, cpam_vsl_km: e.target.value })} className="mt-1" data-testid="settings-cpam-vsl-km" />
          </div>
          <div>
            <Label className="text-xs">Abattement kilométrique (km inclus au forfait)</Label>
            <Input type="number" min="0" step="0.5" value={form.cpam_km_deduction ?? 3}
              onChange={(e) => setForm({ ...form, cpam_km_deduction: e.target.value })} className="mt-1" data-testid="settings-cpam-deduction" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Majoration nuit (%)</Label>
              <Input type="number" min="0" max="100" value={form.cpam_night_pct ?? 50}
                onChange={(e) => setForm({ ...form, cpam_night_pct: e.target.value })} className="mt-1" data-testid="settings-cpam-night" />
            </div>
            <div>
              <Label className="text-xs">Majoration dim./férié (%)</Label>
              <Input type="number" min="0" max="100" value={form.cpam_sunday_pct ?? 25}
                onChange={(e) => setForm({ ...form, cpam_sunday_pct: e.target.value })} className="mt-1" data-testid="settings-cpam-sunday" />
            </div>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Transport partagé : abattement automatique de la convention appliqué au calcul (−25 % à 2 patients, −40 % à 3).
          Montants indicatifs à rapprocher de votre logiciel de télétransmission agréé.
        </p>
        <div className="border-t border-border pt-3 space-y-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={form.cpam_short_enabled !== false} onCheckedChange={(v) => setForm({ ...form, cpam_short_enabled: !!v })} data-testid="settings-cpam-short-toggle" />
            <span className="font-medium">Valorisation « trajet court » ambulance (avenant 11)</span>
          </label>
          {form.cpam_short_enabled !== false && (
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">≤ 5 km (€)</Label>
                <Input type="number" min="0" step="0.01" value={form.cpam_short_t1 ?? 9.75}
                  onChange={(e) => setForm({ ...form, cpam_short_t1: e.target.value })} className="mt-1" data-testid="settings-cpam-short-t1" />
              </div>
              <div>
                <Label className="text-xs">5 à 10 km (€)</Label>
                <Input type="number" min="0" step="0.01" value={form.cpam_short_t2 ?? 7.53}
                  onChange={(e) => setForm({ ...form, cpam_short_t2: e.target.value })} className="mt-1" data-testid="settings-cpam-short-t2" />
              </div>
              <div>
                <Label className="text-xs">10 à 15 km (€)</Label>
                <Input type="number" min="0" step="0.01" value={form.cpam_short_t3 ?? 5.47}
                  onChange={(e) => setForm({ ...form, cpam_short_t3: e.target.value })} className="mt-1" data-testid="settings-cpam-short-t3" />
              </div>
              <div>
                <Label className="text-xs">15 à 19 km (€)</Label>
                <Input type="number" min="0" step="0.01" value={form.cpam_short_t4 ?? 3.42}
                  onChange={(e) => setForm({ ...form, cpam_short_t4: e.target.value })} className="mt-1" data-testid="settings-cpam-short-t4" />
              </div>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            Supplément dégressif ajouté au montant CPAM des courses AMBULANCE uniquement, selon la distance parcourue
            (au-delà de 19 km : aucun supplément). Barème avenant n°11 au 01/01/2025 — modifiable.
          </p>
        </div>
        <Button onClick={save} data-testid="settings-cpam-save">Enregistrer</Button>
      </Card>

      <SefiTemplateCard form={form} setForm={setForm} />

      <Card className="p-6 rounded-md shadow-none space-y-3" data-testid="settings-maps-card">
        <div>
          <h2 className="font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Cartographie &amp; API Google Maps</h2>
          <p className="text-sm text-muted-foreground">
            PACEMIA utilise <strong>Google Maps</strong> pour l'autocomplétion des adresses, le géocodage et le calcul d'itinéraire.
            En cas d'indisponibilité (quota, panne), un repli automatique sur <strong>OpenStreetMap</strong> garantit la continuité — sans coût.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-md border border-border p-3">
            <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Autocomplétion (Places)</div>
            <div className="text-lg font-semibold" data-testid="settings-maps-places">{form.gmaps_calls_places || 0}</div>
            <div className="text-[11px] text-muted-foreground">appels cumulés · ≈ 0,003 $ / session</div>
          </div>
          <div className="rounded-md border border-border p-3">
            <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Détails du lieu</div>
            <div className="text-lg font-semibold" data-testid="settings-maps-details">{form.gmaps_calls_places_details || 0}</div>
            <div className="text-[11px] text-muted-foreground">sélections d'adresse Google</div>
          </div>
          <div className="rounded-md border border-border p-3">
            <div className="text-[11px] text-muted-foreground uppercase tracking-wide">État actuel</div>
            <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">Connecté</div>
            <div className="text-[11px] text-muted-foreground">clé API valide · repli OSM disponible</div>
          </div>
        </div>
      </Card>

      <Card className="p-6 rounded-md shadow-none space-y-3">
        <div>
          <h2 className="font-semibold flex items-center gap-2"><Bell className="h-4 w-4 text-primary" /> Notifications</h2>
          <p className="text-sm text-muted-foreground">Choisissez les évènements qui déclenchent une notification push.</p>
        </div>
        {notif && NOTIF_EVENTS.map((e) => (
          <label key={e.key} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`notif-row-${e.key}`}>
            <Checkbox checked={!!notif[e.key]} onCheckedChange={(v) => saveNotif({ ...notif, [e.key]: !!v })} data-testid={`notif-${e.key}`} />
            {e.label}
          </label>
        ))}
      </Card>

      <Card className="p-6 rounded-md shadow-none space-y-4" data-testid="settings-establishments-card">
        <div>
          <h2 className="font-semibold flex items-center gap-2"><Hospital className="h-4 w-4 text-primary" /> Établissements de santé</h2>
          <p className="text-sm text-muted-foreground">Constituez la liste des hôpitaux, cliniques, cabinets et maisons médicales que vous desservez. Elle alimente le menu « Établissement » de Jeregul. Recherchez automatiquement autour de votre société (OpenStreetMap) ou ajoutez-les manuellement.</p>
        </div>
        <EstablishmentManager reloadSignal={estabReload} />
      </Card>

      <Card className="p-6 rounded-md shadow-none space-y-4" data-testid="settings-demo-card">
        <div>
          <h2 className="font-semibold flex items-center gap-2"><Database className="h-4 w-4 text-primary" /> Jeu de démonstration</h2>
          <p className="text-sm text-muted-foreground">Générez un jeu de données réaliste (50 patients + une cinquantaine de courses du jour, régulées et mutualisées) pour tester l'application ou faire une démonstration. « Supprimer » retire uniquement ces données de démo.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => demoData("generate")} disabled={demoBusy} data-testid="settings-demo-generate">
            {demoBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />}
            Générer un jeu de démo
          </Button>
          <Button variant="outline" onClick={() => demoData("clear")} disabled={demoBusy} data-testid="settings-demo-clear">
            Supprimer le jeu de démo
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">Les courses sont créées pour la date du jour et régulées automatiquement. Consultez-les dans Jeregul / Planning.</p>
      </Card>

      {!user?.acting_staff && (
        <Card className="p-6 rounded-md shadow-none space-y-4" data-testid="settings-staff-card">
          <div>
            <h2 className="font-semibold flex items-center gap-2"><UserCog className="h-4 w-4 text-primary" /> Personnel — comptes à accès limité</h2>
            <p className="text-sm text-muted-foreground">Créez des logins pour votre personnel (secrétaire, régulateur…) avec un accès restreint aux modules de votre choix. Ils se connectent avec leur email sur la page de connexion habituelle.</p>
          </div>
          <StaffManager />
        </Card>
      )}

      <Card className="p-6 rounded-md shadow-none space-y-4">
        <div>
          <h2 className="font-semibold flex items-center gap-2"><Lock className="h-4 w-4 text-primary" /> Code d'accès aux sections</h2>
          <p className="text-sm text-muted-foreground">Protégez certaines sections (ex : Abonnement) par un code à saisir.</p>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={lockEnabled} disabled={!lockLoaded} onCheckedChange={(v) => setLockEnabled(!!v)} data-testid="lock-enabled" />
          <span className="text-sm">Activer la protection par code</span>
        </label>

        {lockEnabled && (
          <>
            <div>
              <Label>Code d'accès {`(laisser vide pour conserver l'actuel)`}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••" className="mt-1" data-testid="lock-password" />
            </div>
            <div className="space-y-2">
              <Label>Sections à protéger</Label>
              <div className="grid grid-cols-2 gap-2">
                {SECTIONS.map((s) => (
                  <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={lockedSections.includes(s.key)} onCheckedChange={() => toggleSection(s.key)} data-testid={`lock-section-${s.key}`} />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        <Button onClick={saveLock} disabled={savingLock} data-testid="lock-save">
          {savingLock ? "Enregistrement…" : "Enregistrer la protection"}
        </Button>
      </Card>

      <Card className="p-6 rounded-md shadow-none space-y-4">
        <div>
          <h2 className="font-semibold flex items-center gap-2"><Smartphone className="h-4 w-4 text-primary" /> Application chauffeur (PWA)</h2>
          <p className="text-sm text-muted-foreground">Partagez ce lien (ou le QR code) à vos chauffeurs pour qu'ils installent l'app sur leur téléphone.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=6&data=${encodeURIComponent(`${window.location.origin}/install`)}`}
            alt="QR installation PACEMIA" width={120} height={120} className="rounded-lg border border-border" data-testid="settings-install-qr"
          />
          <div className="flex-1 w-full space-y-2">
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-md bg-secondary px-3 py-2 text-xs">{`${window.location.origin}/install`}</code>
              <Button variant="outline" size="icon" data-testid="settings-install-copy"
                onClick={async () => { try { await navigator.clipboard.writeText(`${window.location.origin}/install`); toast.success("Lien copié"); } catch { toast.info(`${window.location.origin}/install`); } }}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <a href="/install" target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline" data-testid="settings-install-open">Ouvrir la page d'installation →</a>
          </div>
        </div>
      </Card>
    </div>
  );
}


function SefiTemplateCard({ form, setForm }) {
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);
  const cols = form.sefi_columns || [];
  const upload = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/company/sefi-template", fd);
      setForm({ ...form, sefi_columns: data.columns, sefi_delimiter: data.delimiter, sefi_template_name: data.filename });
      toast.success(`Modèle SEFI enregistré (${data.columns.length} colonnes)`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const clear = async () => {
    try {
      await api.delete("/company/sefi-template");
      setForm({ ...form, sefi_columns: [], sefi_delimiter: ";", sefi_template_name: "" });
      toast.success("Modèle SEFI supprimé");
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Card className="p-6 rounded-md shadow-none space-y-4" data-testid="settings-sefi-card">
      <div>
        <h2 className="font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Facturation SEFI (télétransmission CPAM)</h2>
        <p className="text-sm text-muted-foreground">
          Importez un exemple de fichier fourni par votre logiciel SEFI (CSV). PACEMIA détecte automatiquement l'ordre
          des colonnes attendues et générera vos exports facturables au format exact de SEFI, prêts à être importés.
        </p>
        <p className="text-[11px] text-muted-foreground mt-1">
          Note : la télétransmission finale reste effectuée depuis votre poste équipé SESAM-Vitale (norme B2 + cartes CPS/Vitale).
        </p>
      </div>
      {cols.length > 0 ? (
        <div className="space-y-2">
          <div className="rounded border border-emerald-400/40 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 text-sm" data-testid="settings-sefi-status">
            <div className="font-medium text-emerald-700 dark:text-emerald-300">Modèle SEFI actif</div>
            <div className="text-xs text-muted-foreground">Fichier : {form.sefi_template_name || "—"} · Séparateur : « {form.sefi_delimiter || ";"} » · {cols.length} colonne(s)</div>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="settings-sefi-cols">
            {cols.map((c) => (
              <span key={c} className="text-[11px] px-2 py-0.5 rounded bg-muted border border-border">{c}</span>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="settings-sefi-file" />
            <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="settings-sefi-replace">
              {uploading ? "Import…" : "Remplacer le modèle"}
            </Button>
            <Button variant="ghost" onClick={clear} className="text-destructive" data-testid="settings-sefi-clear">Supprimer</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="settings-sefi-file" />
          <Button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="settings-sefi-upload">
            {uploading ? "Import du fichier…" : "Importer un fichier SEFI (.csv)"}
          </Button>
          <p className="text-[11px] text-muted-foreground">
            Formats acceptés : CSV avec ; ou , comme séparateur, en-tête sur la première ligne, encodage UTF-8 ou Latin-1.
            Une fois le modèle enregistré, un profil « SEFI » apparaît dans Export &amp; Facturation.
          </p>
        </div>
      )}
    </Card>
  );
}
